/**
 * Modelos del buscador público de propiedades.
 * Espejan los DTOs del backend NestJS:
 *   - `PropiedadPublicaRespuestaDto`
 *   - `PinMapaRespuestaDto` / `MapaRespuestaDto` (endpoint BE-04)
 *   - `FiltrarPropiedadesPublicoDto` / `FiltrarMapaPublicoDto`
 */

export enum TipoPropiedad {
  CASA = 'casa',
  DEPARTAMENTO = 'departamento',
  INDUSTRIA = 'industria',
  TERRENO = 'terreno',
  LOCAL = 'local',
  OFICINA = 'oficina',
  ESPECIAL = 'especial',
}

export enum Operacion {
  VENTA = 'venta',
  ALQUILER = 'alquiler',
  AMBOS = 'ambos',
}

export enum Moneda {
  ARS = 'ARS',
  USD = 'USD',
}

export const TIPO_PROPIEDAD_LABELS: Record<TipoPropiedad, string> = {
  [TipoPropiedad.CASA]: 'Casas',
  [TipoPropiedad.DEPARTAMENTO]: 'Departamentos',
  [TipoPropiedad.INDUSTRIA]: 'Industrias',
  [TipoPropiedad.TERRENO]: 'Terrenos',
  [TipoPropiedad.LOCAL]: 'Locales',
  [TipoPropiedad.OFICINA]: 'Oficinas',
  [TipoPropiedad.ESPECIAL]: 'Especiales',
};

/** Singular, para el título de la ficha ("Departamento, Belgrano"). */
export const TIPO_PROPIEDAD_SINGULAR: Record<TipoPropiedad, string> = {
  [TipoPropiedad.CASA]: 'Casa',
  [TipoPropiedad.DEPARTAMENTO]: 'Departamento',
  [TipoPropiedad.INDUSTRIA]: 'Industria',
  [TipoPropiedad.TERRENO]: 'Terreno',
  [TipoPropiedad.LOCAL]: 'Local',
  [TipoPropiedad.OFICINA]: 'Oficina',
  [TipoPropiedad.ESPECIAL]: 'Especial',
};

/** Característica numérica que define cada categoría. */
export type CaracteristicaPropiedad = 'dormitorios' | 'ambientes' | 'superficie';

/**
 * Qué característica corresponde a cada categoría. Espeja
 * `CARACTERISTICA_POR_TIPO` del backoffice (`models/propiedades.model.ts`),
 * que a su vez sale de la regla del backend `validarCamposPorTipo`: es la que
 * decide qué campo se **exige** al cargar la propiedad, así que es también la
 * única que tiene dato para filtrar.
 *
 * "Especial" no exige ninguna, por eso no tiene filtro numérico (FP-38).
 */
const CARACTERISTICA_POR_TIPO: Record<
  TipoPropiedad,
  CaracteristicaPropiedad | null
> = {
  [TipoPropiedad.CASA]: 'dormitorios',
  [TipoPropiedad.DEPARTAMENTO]: 'ambientes',
  [TipoPropiedad.INDUSTRIA]: 'superficie',
  [TipoPropiedad.TERRENO]: 'superficie',
  [TipoPropiedad.LOCAL]: 'superficie',
  [TipoPropiedad.OFICINA]: 'superficie',
  [TipoPropiedad.ESPECIAL]: null,
};

/**
 * `null` sin categoría elegida: con "Todas" no se muestra filtro numérico
 * (decisión D12) porque no hay un campo común a todas las categorías.
 */
export function caracteristicaDeTipo(
  tipo?: TipoPropiedad | null,
): CaracteristicaPropiedad | null {
  return tipo ? (CARACTERISTICA_POR_TIPO[tipo] ?? null) : null;
}

export const CARACTERISTICA_LABELS: Record<CaracteristicaPropiedad, string> = {
  dormitorios: 'Dormitorios',
  ambientes: 'Ambientes',
  superficie: 'Superficie',
};

/**
 * Valor del select a partir del cual se filtra "o más" (se manda sólo el
 * mínimo). Con 5 el select es 1 · 2 · 3 · 4 · +5.
 */
export const AMBIENTES_MAS_DE = 5;

/**
 * Tope del slider de superficie, en m². **Fijo por decisión de negocio**
 * (2026-08-11): no hay endpoint que devuelva el máximo real publicado.
 *
 * Consecuencia: una propiedad de más de 3000 m² no se alcanza desde el
 * buscador. El backend no pone `@Max` en la banda, así que subir este número
 * es lo único que hace falta si negocio lo pide.
 */
export const SUPERFICIE_MAX = 3000;

/** Paso del slider de superficie, en m². */
export const SUPERFICIE_STEP = 50;

export const OPERACION_LABELS: Record<Operacion, string> = {
  [Operacion.VENTA]: 'Venta',
  [Operacion.ALQUILER]: 'Alquiler',
  [Operacion.AMBOS]: 'Venta y Alquiler',
};

/**
 * Viñeta de contacto: todo sale del broker titular del grupo (logo, nombre de
 * la inmobiliaria, WhatsApp y nombre). El agente asignado no se expone en la
 * web pública.
 */
export interface ContactoPublico {
  inmobiliaria?: string;
  /** Con el storage mock del backend la URL es stub:// (no renderizable). */
  logoUrl?: string;
  whatsapp?: string;
  /**
   * Nombre completo del broker titular, pese al nombre histórico de la clave.
   * En la ficha PDF es el respaldo de `inmobiliaria` (issue #58).
   */
  agente?: string;
}

// ─── Imágenes ────────────────────────────────────────────────────────────────
// Mismo contrato y mismos helpers que el backoffice
// (`frontend-backoffice/src/models/propiedades.model.ts`).

/** URLs de una variante de foto en sus dos formatos (para `<picture>`). */
export interface FormatosVariante {
  webp: string;
  avif: string;
}

/**
 * Variantes por tamaño que genera el pipeline de imágenes del backend. El
 * número es el lado MAYOR en px; las fotos se redimensionan sin recortar ni
 * agrandar, así que una foto chica puede pesar lo mismo en las tres.
 */
export interface VariantesImagen {
  /** 1920 px — galería a pantalla completa. */
  full: FormatosVariante;
  /** 800 px — tarjetas del listado y foto principal de la ficha. */
  card: FormatosVariante;
  /** 400 px — miniaturas. */
  thumb: FormatosVariante;
}

export type VarianteFoto = keyof VariantesImagen;

/** Una foto de la galería tal como la devuelve `GET /publico/propiedades/:id`. */
export interface ImagenPublica {
  /** URL de la variante principal. Es el único campo garantizado. */
  url: string;
  /**
   * AUSENTE en imágenes cargadas antes de que el backend tuviera el pipeline.
   * No asumir que viene: usar `fuentesFoto()`, que resuelve el fallback.
   */
  variantes?: VariantesImagen;
}

/**
 * Fuentes de una foto para `<picture>`: la URL WebP (que va en el `<img>`) y
 * la AVIF (que va en un `<source>`, para que el navegador que la soporte se
 * lleve el archivo más liviano y el resto caiga a WebP).
 */
export interface FuentesFoto {
  /** Siempre presente: es el `src` del `<img>`. */
  src: string;
  /** Ausente cuando no hay variantes (imágenes previas al pipeline). */
  avif?: string;
}

/**
 * Arma las fuentes de una foto en la variante que corresponde al tamaño con
 * el que se va a renderizar. Elegir la variante es del consumidor.
 *
 * **Sobre el fallback:** si `variantes` viene ausente se usa `url`, y eso NO
 * es la variante `full`. El fallback existe para las imágenes anteriores al
 * pipeline, donde `url` apunta al archivo original tal cual se subió: sin
 * auto-rotación por EXIF y sin strip de metadatos. Además de pesar más, puede
 * verse distinto (p. ej. una foto de celular rotada). No hay nada que el
 * frontend pueda hacer: se corrige re-subiendo la foto o con un backfill.
 */
export function fuentesFoto(
  imagen: ImagenPublica,
  variante: VarianteFoto,
): FuentesFoto {
  const formatos = imagen.variantes?.[variante];
  return formatos
    ? { src: formatos.webp, avif: formatos.avif }
    : { src: imagen.url };
}

/** Tarjeta pública de propiedad (sin datos del propietario ni internos). */
export interface PropiedadPublica {
  id: string;
  tipoPropiedad: TipoPropiedad;
  operacion: Operacion;
  localidad: string;
  direccion: string;
  dormitorios?: number;
  ambientes?: number;
  superficie?: number;
  precioAlquiler?: number;
  monedaAlquiler?: Moneda;
  precioVenta?: number;
  monedaVenta?: Moneda;
  descripcion?: string;
  /** Links de video del aviso (spec §7.1: Instagram, TikTok y YouTube) */
  instagram?: string;
  tiktok?: string;
  youtube?: string;
  emprendimiento: boolean;
  latitud?: number;
  longitud?: number;
  imagenPortada?: string;
  /** Variantes de la portada. Ausentes en imágenes previas al pipeline. */
  imagenPortadaVariantes?: VariantesImagen;
  contacto?: ContactoPublico;
}

/** Ficha pública (`GET /publico/propiedades/:id`): tarjeta + galería. */
export interface PropiedadDetalle extends PropiedadPublica {
  /**
   * URLs planas de la galería, portada primero (máx. 5 — spec §7.1).
   *
   * El backend sigue devolviéndolo, pero `galeria` es lo que hay que usar:
   * trae las mismas fotos con sus variantes. Se conserva como respaldo por si
   * una respuesta viniera sin `galeria`.
   */
  imagenes: string[];
  /** Galería con variantes por foto, en el mismo orden que `imagenes`. */
  galeria?: ImagenPublica[];
}

/**
 * Item del listado (`GET /publico/propiedades`): la tarjeta más su galería,
 * para que el carrusel del `list card` (Figma `244:1572`) tenga las N fotos
 * sin pedir el detalle de cada resultado.
 *
 * `galeria` NO está en `PropiedadPublica` porque esa misma interfaz es la del
 * pin del mapa (`PinMapa.tarjeta`), que a propósito no la lleva: el mapa
 * devuelve cientos de pines y se repide en cada pan/zoom. Espeja
 * `PropiedadListadoPublicaRespuestaDto` del backend.
 */
export interface PropiedadListado extends PropiedadPublica {
  /**
   * Portada primero, máx. 5 (spec §7.1). Array vacío si la propiedad no tiene
   * fotos; `imagenPortada` sigue estando como respaldo.
   */
  galeria: ImagenPublica[];
}

export interface ListaPropiedadesPublica {
  datos: PropiedadListado[];
  total: number;
  pagina: number;
  porPagina: number;
  totalPaginas: number;
}

/** Pin del mapa (BE-04): coordenadas + precio + datos de la tarjeta. */
export interface PinMapa {
  id: string;
  latitud: number;
  longitud: number;
  operacion: Operacion;
  precioVenta?: number;
  monedaVenta?: Moneda;
  precioAlquiler?: number;
  monedaAlquiler?: Moneda;
  tarjeta: PropiedadPublica;
}

export interface MapaRespuesta {
  pines: PinMapa[];
  total: number;
  limite: number;
  /** true si hay más resultados que el límite: acotar viewport o subir zoom */
  truncado: boolean;
}

/** Bounding box del viewport del mapa (BE-06). */
export interface BoundingBox {
  latMin: number;
  latMax: number;
  lngMin: number;
  lngMax: number;
}

// ─── Rango de precios (BP-01) ────────────────────────────────────────────────

/**
 * Extremos de precio de una moneda. `min` viene redondeado a miles hacia abajo
 * y `max` hacia arriba; los dos son `null` cuando no hay propiedades con ese
 * precio en esa moneda.
 *
 * **`min` puede ser `0` con datos reales** —cualquier precio menor a 1000
 * redondea a 0, y un alquiler de USD 800 es corriente—, así que hay que
 * chequearlo con `!= null` y nunca por veracidad: `if (extremos.min)` descarta
 * un 0 legítimo.
 */
export interface ExtremosPrecio {
  min: number | null;
  max: number | null;
}

export type ExtremosPorMoneda = Record<Moneda, ExtremosPrecio>;

/** Respuesta de `GET /publico/propiedades/rango-precios`. */
export interface RangoPrecios {
  venta: ExtremosPorMoneda;
  alquiler: ExtremosPorMoneda;
}

/**
 * Filtros del buscador (RN02: listado y mapa comparten los mismos filtros).
 */
export interface FiltrosBusqueda {
  tipoPropiedad?: TipoPropiedad;
  operacion?: Operacion;
  /**
   * Zonas buscadas (DP02/DP03, búsqueda multi-zona). El backend recibe el
   * parámetro `localidad` repetido y combina las zonas con OR.
   */
  localidades?: string[];
  precioMin?: number;
  precioMax?: number;
  monedaVenta?: Moneda;
  precioAlquilerMin?: number;
  precioAlquilerMax?: number;
  monedaAlquiler?: Moneda;
  buscar?: string;
  /**
   * 1–4 exacto, 5 = "+5". Se mapea a `ambientesMin`/`ambientesMax` del
   * backend, que filtra sobre `COALESCE(ambientes, dormitorios)`: la categoría
   * elegida define si eso son dormitorios (Casa) o ambientes (Departamento).
   */
  ambientes?: number;
  /**
   * Banda de superficie en m² (BP-04), para las categorías que se filtran por
   * superficie. Ausentes mientras el usuario no toque el slider: mandarlas en
   * reposo dejaría afuera todas las propiedades sin superficie cargada.
   */
  superficieMin?: number;
  superficieMax?: number;
  /**
   * Solo emprendimientos. En el backend es un flag ortogonal al tipo
   * (spec §7.1); la web pública lo presenta como una categoría más del
   * buscador (caso especial: excluyente con `tipoPropiedad` en la UI).
   */
  emprendimiento?: boolean;
}

const FORMATO_PRECIO = new Intl.NumberFormat('es-AR', {
  maximumFractionDigits: 0,
});

/** Formatea un precio en su moneda original: `$ 350.000` / `U$S 120.000`. */
/**
 * Precio partido en moneda e importe. La ficha los necesita separados porque
 * pinta la moneda en `Regular` y el importe en
 * `SemiBold`, dentro del mismo estilo `Display 2`.
 */
export function partesPrecio(
  valor: number,
  moneda?: Moneda,
): { simbolo: string; monto: string } {
  return {
    simbolo: moneda === Moneda.USD ? 'U$S' : '$',
    monto: FORMATO_PRECIO.format(valor),
  };
}

export function formatearPrecio(valor: number, moneda?: Moneda): string {
  const { simbolo, monto } = partesPrecio(valor, moneda);
  return `${simbolo} ${monto}`;
}

/**
 * Precio principal a mostrar en el pin (FE-03): si la operación incluye
 * venta se muestra el precio de venta; si no, el de alquiler.
 */
export function precioPrincipal(pin: {
  operacion: Operacion;
  precioVenta?: number;
  monedaVenta?: Moneda;
  precioAlquiler?: number;
  monedaAlquiler?: Moneda;
}): string {
  if (pin.operacion !== Operacion.ALQUILER && pin.precioVenta != null) {
    return formatearPrecio(pin.precioVenta, pin.monedaVenta);
  }
  if (pin.precioAlquiler != null) {
    return formatearPrecio(pin.precioAlquiler, pin.monedaAlquiler);
  }
  return 'Consultar';
}

/** Las pestañas de precio de la tarjeta, en el orden en que se pintan (FP-49). */
export interface PreciosCard {
  /** Pestaña de la derecha: la venta o, con una sola operación, el único precio. */
  precio: string;
  /** Pestaña de la izquierda: el alquiler. Sólo cuando la propiedad tiene los dos. */
  precioAlquiler?: string;
}

/**
 * Precios a mostrar en la tarjeta (FP-49, trasladado del backoffice).
 *
 * Con venta y alquiler cargados van los dos: venta a la derecha, alquiler a la
 * izquierda. En cualquier otro caso queda una sola pestaña, con exactamente el
 * precio que venía mostrando la tarjeta —de ahí que ese camino delegue en
 * `precioPrincipal`, que además es el que usa el chip del pin del mapa, donde
 * no hay lugar para dos importes.
 */
export function preciosDeCard(p: {
  operacion: Operacion;
  precioVenta?: number;
  monedaVenta?: Moneda;
  precioAlquiler?: number;
  monedaAlquiler?: Moneda;
}): PreciosCard {
  const venta =
    p.operacion !== Operacion.ALQUILER && p.precioVenta != null
      ? formatearPrecio(p.precioVenta, p.monedaVenta)
      : null;
  const alquiler =
    p.operacion !== Operacion.VENTA && p.precioAlquiler != null
      ? formatearPrecio(p.precioAlquiler, p.monedaAlquiler)
      : null;

  if (venta && alquiler) return { precio: venta, precioAlquiler: alquiler };
  return { precio: precioPrincipal(p) };
}

/**
 * Link de consulta por WhatsApp: al número del agente asignado si está
 * disponible (definición de negocio); si no, compartir genérico.
 */
export function armarLinkWhatsApp(
  whatsapp: string | undefined,
  texto: string,
): string {
  const numero = whatsapp?.replace(/\D/g, '') ?? '';
  const query = `text=${encodeURIComponent(texto)}`;
  return numero
    ? `https://wa.me/${numero}?${query}`
    : `https://wa.me/?${query}`;
}

/** true si la URL puede usarse en un <img> (descarta stub:// del mock). */
export function esUrlRenderizable(url?: string): boolean {
  return !!url && /^https?:\/\//.test(url);
}

/**
 * Normaliza un texto opcional del contacto: en blanco cuenta como ausente.
 *
 * El DTO público no recorta los strings, así que un campo cargado con espacios
 * (p. ej. `empresa: '   '` del broker) llega como texto truthy: los `@else if`
 * de la viñeta y de la hoja lo toman por válido y pintan un rótulo vacío en
 * lugar de caer al fallback. Mismo criterio que la hoja del backoffice.
 */
export function textoODefault(valor?: string | null): string | null {
  return (valor ?? '').trim() || null;
}

